import React, { useEffect, useState } from 'react';
import {
  ShieldCheck, Save, Loader2, AlertTriangle, Eye, EyeOff, TestTube2,
  RefreshCw, CheckCircle2, XCircle, Info, ExternalLink,
} from 'lucide-react';
import { Card } from '../components/Card';
import { SectionHeader } from '../components/Tabs';
import { Badge } from '../components/Badge';
import { Button } from '../components/Button';
import { Field, Input } from '../components/Input';
import { OperatorStat } from '../components/OperatorStat';
import { adminFetch } from '../adminApi';

/**
 * CaptchaSolverPage — operator-only page to manage the NopeCHA
 * captcha provider settings. Customers never see this page; they
 * just set `captcha_solver: true` on /v1/fetch.
 *
 * Settings persist to the operator_config KV store via:
 *   GET    /admin/captcha-solver       — read provider + key hint
 *   PATCH  /admin/captcha-solver       — update provider + key
 *   POST   /admin/captcha-solver/test  — round-trip smoke test
 *
 * All endpoints require admin-key auth. The api key is never echoed
 * back in GET; only a hint string like "nopecha=<set>" is returned.
 */

const PROVIDERS = [
  {
    value: 'disabled',
    label: 'Disabled (no solver)',
    desc: 'Customers who set captcha_solver=true will get a clear 503 with setup instructions.',
  },
  {
    value: 'nopecha',
    label: 'NopeCHA (image recognition)',
    desc: 'Sends screenshots of captcha widgets to NopeCHA. Works on textcaptcha and image-grid challenges. Sign up at nopecha.com — cheapest plan is $4.99/mo for 2,000 solves/day.',
  },
];

export default function CaptchaSolverPage() {
  const [config, setConfig] = useState({ provider_name: 'disabled', api_key_hint: '' });
  const [draft, setDraft] = useState({ provider_name: 'disabled', api_key: '' });
  const [showKey, setShowKey] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);  // { ok, message, ... }
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const data = await adminFetch('/admin/captcha-solver');
      setConfig(data);
      setDraft({ provider_name: data.provider_name, api_key: '' });
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); }, []);

  async function save() {
    setSaving(true);
    setError(null);
    try {
      const body = { provider_name: draft.provider_name };
      // Only send api_key if the operator typed one (empty = no change)
      if (draft.api_key && draft.api_key.trim()) {
        body.api_key = draft.api_key.trim();
      }
      const r = await adminFetch('/admin/captcha-solver', {
        method: 'PATCH',
        body,
      });
      setConfig(r);
      setDraft({ provider_name: r.provider_name, api_key: '' });
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setSaving(false);
    }
  }

  async function toggle() {
    // One-click enable / disable. We snapshot whether the key is set
    // so we can preserve it across the toggle: disabling must not
    // blank the stored key (the operator only rotates the key
    // deliberately via the field).
    setSaving(true);
    setError(null);
    try {
      const target = config.provider_name === 'disabled' ? 'nopecha' : 'disabled';
      // If we're enabling back to nopecha and a key was already set
      // (api_key_hint shows '<set>'), we don't need to send one; the
      // backend keeps the stored key when api_key is omitted.
      const r = await adminFetch('/admin/captcha-solver', {
        method: 'PATCH',
        body: { provider_name: target },
      });
      setConfig(r);
      setDraft({ provider_name: r.provider_name, api_key: '' });
      setTestResult(null);
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setSaving(false);
    }
  }

  async function test() {
    setTesting(true);
    setTestResult(null);
    setError(null);
    try {
      const r = await adminFetch('/admin/captcha-solver/test', { method: 'POST' });
      setTestResult({ ok: true, ...r });
    } catch (e) {
      setTestResult({ ok: false, message: e.message || String(e) });
    } finally {
      setTesting(false);
    }
  }

  const dirty =
    draft.provider_name !== config.provider_name ||
    (draft.api_key && draft.api_key.trim().length > 0);

  return (
    <div className="space-y-6">
      <SectionHeader
        title="Captcha solver"
        description="Pick which provider the engine uses when a customer sets captcha_solver=true on /v1/fetch. Customers always pay +5 credits per actual solve."
        actions={
          <div className="flex gap-2">
            <Button
              variant={config.provider_name === 'disabled' ? 'primary' : 'outline'}
              size="sm"
              onClick={toggle}
              disabled={saving || loading}
            >
              {saving ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : config.provider_name === 'disabled' ? (
                <ShieldCheck className="h-3.5 w-3.5" />
              ) : (
                <XCircle className="h-3.5 w-3.5" />
              )}
              {config.provider_name === 'disabled' ? 'Enable nopecha' : 'Disable solver'}
            </Button>
            <Button variant="secondary" size="sm" onClick={load}>
              <RefreshCw className="h-3.5 w-3.5" />
              Refresh
            </Button>
          </div>
        }
      />

      {/* Status / provider card */}
      <Card>
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-full ${
              config.provider_name === 'disabled' ? 'bg-neutral-100' : 'bg-emerald-100'
            }`}>
              <ShieldCheck className={
                config.provider_name === 'disabled'
                  ? 'text-neutral-500'
                  : 'text-emerald-600'
              } size={20} />
            </div>
            <div>
              <div className="font-medium">Current provider</div>
              <div className="text-sm text-neutral-500 font-mono mt-0.5">
                {config.api_key_hint || 'no key set'}
              </div>
            </div>
          </div>
          <Badge variant={config.provider_name === 'disabled' ? 'neutral' : 'success'}>
            {config.provider_name === 'disabled' ? 'disabled' : 'active'}
          </Badge>
        </div>
      </Card>

      {/* Form */}
      <Card>
        <div className="space-y-4">
          <Field label="Provider">
            <select
              className="w-full px-3 py-2 border border-neutral-300 rounded-md bg-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
              value={draft.provider_name}
              onChange={(e) => setDraft({ ...draft, provider_name: e.target.value })}
              disabled={loading}
            >
              {PROVIDERS.map((p) => (
                <option key={p.value} value={p.value}>{p.label}</option>
              ))}
            </select>
            <p className="text-xs text-neutral-500 mt-1">
              {PROVIDERS.find(p => p.value === draft.provider_name)?.desc}
            </p>
          </Field>

          <Field
            label="API key"
            hint={
              config.api_key_hint && config.api_key_hint.includes('<set>')
                ? 'A key is currently set. Leave blank to keep it; type a new key to rotate.'
                : 'Paste your provider key here. It is never echoed back in the UI.'
            }
          >
            <div className="flex gap-2">
              <Input
                type={showKey ? 'text' : 'password'}
                placeholder={
                  config.api_key_hint && config.api_key_hint.includes('<set>')
                    ? '(unchanged)'
                    : 'paste key here'
                }
                value={draft.api_key}
                onChange={(e) => setDraft({ ...draft, api_key: e.target.value })}
                autoComplete="off"
                className="flex-1"
                disabled={loading}
              />
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowKey((s) => !s)}
                type="button"
                aria-label={showKey ? 'Hide key' : 'Show key'}
              >
                {showKey ? <EyeOff size={16} /> : <Eye size={16} />}
              </Button>
            </div>
          </Field>

          {error && (
            <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">
              <AlertTriangle size={14} />
              <span>{error}</span>
            </div>
          )}

          <div className="flex flex-wrap gap-2 pt-1">
            <Button
              onClick={save}
              disabled={!dirty || saving || loading}
            >
              {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
              Save
            </Button>
            <Button
              variant="secondary"
              onClick={test}
              disabled={testing || config.provider_name === 'disabled' || loading}
            >
              {testing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <TestTube2 className="h-3.5 w-3.5" />}
              Test credentials
            </Button>
          </div>

          {testResult && (
            <div className={`text-sm rounded-md px-3 py-2 border ${
              testResult.ok
                ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                : 'bg-red-50 text-red-700 border-red-200'
            }`}>
              <div className="flex items-center gap-2 font-medium">
                {testResult.ok ? (
                  <CheckCircle2 size={14} />
                ) : (
                  <XCircle size={14} />
                )}
                <span>{testResult.ok ? 'Credentials work' : 'Test failed'}</span>
              </div>
              <div className="mt-1 font-mono text-xs break-all">
                {testResult.ok
                  ? `provider=${testResult.provider}${testResult.job_id ? `, job_id=${testResult.job_id}` : ''}${testResult.class ? `, class=${testResult.class}` : ''}`
                  : testResult.message}
              </div>
            </div>
          )}
        </div>
      </Card>

      {/* Info card */}
      <Card>
        <div className="flex items-start gap-3">
          <Info className="text-neutral-400 mt-0.5" size={16} />
          <div className="text-sm text-neutral-600 space-y-2">
            <div>
              <strong>How customers use it:</strong> They add{' '}
              <code className="px-1 bg-neutral-100 rounded text-xs">
                captcha_solver: true
              </code>{' '}
              to their POST /v1/fetch body. The engine detects hCaptcha /
              reCAPTCHA / Turnstile widgets on the rendered page and sends
              screenshots to the configured provider. The customer is charged
              <strong> +5 credits</strong> per actual solve (post-flight).
            </div>
            <div>
              <strong>Limitations (2026-06):</strong> NopeCHA Recognition works
              best on simple image-grid challenges. Real hCaptcha challenges
              use a JSON object (request_type, tasklist) injected by the
              hCaptcha JS — the screenshot-only path doesn't always solve
              those. The richer path (intercepting the JSON object) is on
              the roadmap.
            </div>
            <div>
              <strong>Get a key:</strong>{' '}
              <a
                href="https://nopecha.com"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-emerald-600 hover:underline"
              >
                nopecha.com
                <ExternalLink size={12} />
              </a>{' '}
              — cheapest is $4.99/mo for 2,000 solves/day.
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
}
